// RITA-1552 — Die Werte, in denen sich „Vertrag erstellen“ und „Rücksendung drucken“ unterscheiden (Skizze Rücklauf vom
// 21.10.2026, Bilder D und E): Arten, Häkchen, Fenstertitel, Fußsatz und Ladetexte des Fensters, Pfad und Titel der
// Bearbeiten-Seite, Such-Parameter ihrer Adresse und die Entwurfs-Dokumente; dazu (Schritt 13, Bild E) die Meldung der
// Bearbeiten-Seite, wenn keine Vorlage des Vorgangs passt. Fenster und Seite lesen diese Werte hier ab. Die Karte liest
// sie nicht: Ihre Knopftexte nimmt sie direkt aus den Texten (KNOPF_TEXT, KNOPF_RUECKSENDUNG), den Such-Parameter für
// die Bearbeiten-Seite bildet sie selbst mit vorgangSuche (services/vorgang.ts). Nicht hier, sondern in den Komponenten
// verzweigen (Abschlussreview MR 2, S2): Symbol und Breite des Fensters und seine Meldung ohne Rücksendungs-Vorlage
// (VertragErstellenDialog), der Vertragsbezug „Gehört zu“ bzw. `expected_contract_entry_id` (VertragErstellenDialog,
// EntwurfBearbeitung, VertragsentwurfPage), die Wahl des Entwurfs in der Karte und die Kennungen der Entwurfszeile
// (EntwurfZeile). Für den Vertrag sind alle Werte die bisherigen (3a–3c).
//
// Diese Datei importiert Texte, Arten und GraphQL-Dokumente; keine dieser Dateien importiert sie (kein Import-Zyklus).
import type { ContractCreationKind } from 'global-intern-types';
import { ENTWURFSDOKUMENTE, Entwurfsdokumente } from '../api/vertragsentwurf';
import { RUECKSENDUNG_FENSTER_TITEL, RUECKSENDUNG_FUSS } from './vertragsdruckTexte';
import {
  FENSTER_TITEL,
  keinRuecksendungsanschreibenText,
  LADEFEHLER_FENSTER,
  LADEFEHLER_RUECKSENDUNG,
  RUECKSENDUNG_WIRD_GELADEN,
  WIRD_GELADEN,
} from './vertragserstellungTexte';
import { FENSTER_FUSS_DRUCK, keineVorlageText, pfadVorgang, seitenTitel } from './vertragsentwurfTexte';
import { Vorgang, VORGANG_ARTEN, vorgangSuche } from './vorgang';

export interface Vorgangskonfiguration {
  vorgang: Vorgang;
  /** Arten des Vorgangs in der Reihenfolge der PDF (Abfrage `kinds`, Spalten, Reiter, Druckeingabe). */
  arten: readonly ContractCreationKind[];
  /** Häkchen je Dokument im Fenster und im Drucken-Fenster der Seite – nur bei mehreren Dokumenten (Bild D, E). */
  haekchen: boolean;
  fensterTitel: string;
  /** Fußsatz des Fensters links neben „Schließen“ und „Drucken“. */
  fensterFuss: string;
  ladetext: string;
  ladefehler: string;
  /** Pfadteil der Bearbeiten-Seite vor „Bearbeiten“ („Vertrag erstellen“ bzw. „Rücksendung drucken“). */
  pfad: string;
  seitenTitel: (beraterName: string) => string;
  /**
   * Meldung der Bearbeiten-Seite, wenn keine Vorlage des Vorgangs passt (`kriterien` wie kriterienText): beim Vertrag
   * Bild E c aus 3b (mit Entwurf und „Der Entwurf bleibt unverändert.“), bei der Rücksendung der Hinweis aus Bild D c
   * der Skizze Rücklauf zeichengleich wie im Fenster.
   */
  keineVorlageText: (beraterName: string, kriterien: string, mitEntwurf: boolean) => string;
  /** Such-Parameter der Bearbeiten-Seite ohne „?“/„&“ (leer beim Vertrag), siehe vorgangSuche. */
  suche: string;
  entwurf: Entwurfsdokumente;
}

const konfiguration = (
  vorgang: Vorgang,
  texte: Pick<Vorgangskonfiguration, 'fensterTitel' | 'fensterFuss' | 'ladetext' | 'ladefehler' | 'keineVorlageText'>,
): Vorgangskonfiguration => ({
  vorgang,
  arten: VORGANG_ARTEN[vorgang],
  haekchen: VORGANG_ARTEN[vorgang].length > 1,
  ...texte,
  pfad: pfadVorgang(vorgang),
  seitenTitel: (beraterName: string) => seitenTitel(beraterName, vorgang),
  suche: vorgangSuche(vorgang),
  entwurf: ENTWURFSDOKUMENTE[vorgang],
});

export const VORGANG_KONFIG: Readonly<Record<Vorgang, Vorgangskonfiguration>> = {
  contract: konfiguration('contract', {
    fensterTitel: FENSTER_TITEL,
    fensterFuss: FENSTER_FUSS_DRUCK,
    ladetext: WIRD_GELADEN,
    ladefehler: LADEFEHLER_FENSTER,
    keineVorlageText,
  }),
  return: konfiguration('return', {
    fensterTitel: RUECKSENDUNG_FENSTER_TITEL,
    fensterFuss: RUECKSENDUNG_FUSS,
    ladetext: RUECKSENDUNG_WIRD_GELADEN,
    ladefehler: LADEFEHLER_RUECKSENDUNG,
    keineVorlageText: (beraterName, kriterien) => keinRuecksendungsanschreibenText(beraterName, kriterien),
  }),
};
